/**
 * B2 · CHAT DEL AULA — query builders Drizzle + query keys.
 *
 * Todo lo que se lee aquí llega por `my_cohort_chat` (alumno) o
 * `my_cohort_chat_teacher` (quien modera). El cliente NO vuelve a filtrar por
 * `archived_at` ni por `hidden_at` para «asegurarse»: esos filtros son control
 * de acceso y viven en las sync rules. Filtrarlos otra vez aquí escondería en
 * la interfaz un fallo del stream justo cuando hace falta verlo.
 *
 * Lo que sí filtra el cliente es `deleted_at` (soft-delete propio del autor),
 * igual que en el resto del repo.
 */

import {
  and,
  asc,
  cohort_chat_message,
  cohort_chat_reaction,
  cohort_chat_read,
  desc,
  eq,
  isNotNull,
  isNull,
  type PowerSyncDrizzleDB,
} from "@space/powersync";

export const classroomChatQueryKeys = {
  all: ["academy-classroom-chat"] as const,
  messages: (cohortId: string | undefined) =>
    ["academy-classroom-chat", "messages", cohortId] as const,
  pinned: (cohortId: string | undefined) => ["academy-classroom-chat", "pinned", cohortId] as const,
  read: (cohortId: string | undefined, userId: string | undefined) =>
    ["academy-classroom-chat", "read", cohortId, userId] as const,
  reactions: (cohortId: string | undefined) =>
    ["academy-classroom-chat", "reactions", cohortId] as const,
};

/** Conversación de una cohorte, en orden cronológico (la ventana viva). */
export function getCohortChatMessagesQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(cohort_chat_message)
    .where(and(eq(cohort_chat_message.cohort_id, cohortId), isNull(cohort_chat_message.deleted_at)))
    .orderBy(asc(cohort_chat_message.created_at));
}

/** Mensajes DESTACADOS de una cohorte (los que no caducan con la ventana). */
export function getPinnedChatMessagesQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(cohort_chat_message)
    .where(
      and(
        eq(cohort_chat_message.cohort_id, cohortId),
        isNull(cohort_chat_message.deleted_at),
        isNotNull(cohort_chat_message.pinned_at),
      ),
    )
    .orderBy(desc(cohort_chat_message.pinned_at));
}

/** MI marca de leído / silencio de un aula (la de otros no se sincroniza). */
export function getMyChatReadQuery(db: PowerSyncDrizzleDB, cohortId: string, userId: string) {
  return db
    .select()
    .from(cohort_chat_read)
    .where(and(eq(cohort_chat_read.cohort_id, cohortId), eq(cohort_chat_read.user_id, userId)))
    .limit(1);
}

/** Reacciones de la ventana viva de una cohorte. */
export function getCohortChatReactionsQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db.select().from(cohort_chat_reaction).where(eq(cohort_chat_reaction.cohort_id, cohortId));
}
